import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaEdit, FaListAlt, FaExternalLinkAlt, FaSpotify, FaSignOutAlt } from 'react-icons/fa';

const logoStyle = {
  position: 'absolute',
  top: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  fontWeight: 700,
  fontSize: '2.5rem',
  color: '#6366f1',
  letterSpacing: 1,
  fontFamily: 'Caveat, cursive',
  textDecoration: 'none',
  zIndex: 10,
  marginBottom: 0,
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function OpenProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrompt, setShowPrompt] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameAvailable, setNameAvailable] = useState(null); // null, true, false
  const [checking, setChecking] = useState(false);
  const [hoveredProjectId, setHoveredProjectId] = useState(null); // <-- add this
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [logoutHover, setLogoutHover] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    navigate('/login');
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const token = sessionStorage.getItem('token');
    fetch(`${API_URL}/api/event-invites/templates`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Unauthorized');
          }
          throw new Error('Network response was not ok');
        }
        return res.json();
      })
      .then((data) => {
        setProjects(data || []);
        setLoading(false);
      })
      .catch((error) => {
        if (error.message === 'Unauthorized') {
          sessionStorage.removeItem('token');
          navigate('/login');
          return;
        }
        setError(error.message);
        setLoading(false);
      });
  }, [navigate]);

  // Check name availability on every keystroke
  useEffect(() => {
    if (!showPrompt) return;
    if (!newName) {
      setNameAvailable(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    setNameAvailable(null);
    const timeout = setTimeout(() => {
      // TODO: Replace with real API call to check name availability
      // For now, just always allow
      setNameAvailable(true);
      setChecking(false);
    }, 250);
    return () => clearTimeout(timeout);
  }, [newName, showPrompt]);

  const handleAddProject = () => {
    if (!newName || !nameAvailable) return;
    setProjects([...projects, { key: newName, playlist_id: null }]);
    setShowPrompt(false);
    setNewName('');
    setNameAvailable(null);
  };

  return (
    <div style={{ padding: '2rem', position: 'relative', minHeight: '100vh' }}>
      <Link to="/" style={{ ...logoStyle, display: 'flex', alignItems: 'center', fontSize: 'unset' }}>
        <img
          src={require('../assets/left_logo.png')}
          alt="Invite Avenue Logo"
          style={{ height: '12rem', objectFit: 'contain', marginLeft: '-3rem', marginTop: '-5rem' }}
        />
      </Link>
      <button
        onClick={handleLogout}
        onMouseEnter={() => setLogoutHover(true)}
        onMouseLeave={() => setLogoutHover(false)}
        style={{
          position: 'absolute',
          top: '1rem',
          right: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 18px',
          borderRadius: '24px',
          background: '#fff',
          border: 'none',
          boxShadow: logoutHover ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s',
          fontSize: '0.95rem',
          color: '#374151',
          fontWeight: 500,
          zIndex: 10,
        }}
      >
        <FaSignOutAlt size={18} />
        Logout
      </button>
      <div style={{ marginTop: '5rem' }}>
        {loading ? (
          <p>Loading projects...</p>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(31vw, 1fr))',
            gap: '1.5rem',
            marginTop: '2rem',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}>
            {projects.map((project) => (
              <div
                key={project.key}
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '180px',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  color: '#333',
                  transition: 'transform 0.15s',
                  cursor: 'pointer',
                  position: 'relative', // for overlay
                  overflow: 'hidden',
                }}
                onMouseEnter={() => setHoveredProjectId(project.key)}
                onMouseLeave={() => setHoveredProjectId(null)}
                onClick={() => {
                  if (hoveredProjectId !== project.key) {
                    navigate(`/invites/${encodeURIComponent(project.key)}/editor`);
                  }
                }}
              >
                {/* Only show name when not hovered */}
                {hoveredProjectId !== project.key && (
                  <span style={{
                    fontSize: '4.4rem',
                    fontWeight: 700,
                    fontFamily: 'Caveat, cursive, sans-serif',
                    color: '#000000',
                    letterSpacing: 1,
                    textAlign: 'center',
                    width: '100%',
                    lineHeight: 1.1,
                    wordBreak: 'break-word',
                  }}>{project.key}</span>
                )}
                {hoveredProjectId === project.key && (
                  <>
                    <div style={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      background: 'rgba(0,0,0,0.25)',
                      zIndex: 1,
                    }} />
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      gap: '2.5rem',
                      zIndex: 2,
                    }}>
                      <div style={{
                        background: '#fff',
                        borderRadius: '50%',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                        width: 70,
                        height: 70,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s',
                      }}
                        title="Open Editor"
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/invites/${encodeURIComponent(project.key)}/editor`);
                        }}
                      >
                        <FaEdit
                          size={44}
                          color="#000000"
                        />
                      </div>
                      {project.containsResponses && (
                        <div style={{
                          background: '#fff',
                          borderRadius: '50%',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                          width: 70,
                          height: 70,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'box-shadow 0.2s',
                        }}
                          title="View Responses"
                          onClick={e => {
                            e.stopPropagation();
                            navigate(`/invites/${encodeURIComponent(project.key)}/responses`);
                          }}
                        >
                          <FaListAlt
                            size={44}
                            color="#000000"
                          />
                        </div>
                      )}
                      <div style={{
                        background: '#fff',
                        borderRadius: '50%',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                        width: 70,
                        height: 70,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s',
                      }}
                        title="View Live Website"
                        onClick={e => {
                          e.stopPropagation();
                          const currentUrl = new URL(window.location);
                          const subdomainUrl = `${currentUrl.protocol}//${encodeURIComponent(project.key)}.${currentUrl.hostname}${currentUrl.port ? ':' + currentUrl.port : ''}`;
                          window.open(subdomainUrl, '_blank');
                        }}
                      >
                        <FaExternalLinkAlt
                          size={44}
                          color="#000000"
                        />
                      </div>
                      {project.playlist_id && (
                        <div style={{
                          background: '#191414',
                          borderRadius: '50%',
                          // boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                          width: 70,
                          height: 70,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'box-shadow 0.2s',
                        }}
                          title="Open Spotify Playlist"
                          onClick={e => {
                            e.stopPropagation();
                            window.open(`https://open.spotify.com/playlist/${project.playlist_id}`, '_blank');
                          }}
                        >
                          <FaSpotify
                            size={70}
                            color="#1DB954"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
            {/* Add Project Card */}
            <div
              style={{
                background: '#f3f4f6',
                border: '2px dashed #000000',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '180px',
                fontSize: '2.5rem',
                color: '#000000',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseOver={e => e.currentTarget.style.background = '#e0e7ff'}
              onMouseOut={e => e.currentTarget.style.background = '#f3f4f6'}
              onClick={() => setShowPrompt(true)}
            >
              <span style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>+</span>
              <span style={{ fontSize: '1.1rem', fontWeight: 500 }}>Add Project</span>
            </div>
          </div>
        )}
      </div>
      {/* Modal Prompt */}
      {showPrompt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '1rem',
            padding: '2.5rem 2rem',
            minWidth: 320,
            boxShadow: '0 8px 32px rgba(60,72,100,0.12)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>Create New Invitation</h2>
            <div style={{ position: 'relative', width: '100%', marginBottom: '1.5rem' }}>
              <input
                type="text"
                placeholder="Project name"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                style={{
                  padding: '0.75rem 2.5rem 0.75rem 0.75rem', // right padding for icon
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e7eb',
                  fontSize: '1rem',
                  outline: 'none',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />
              {newName && !checking && nameAvailable === true && (
                <FaCheckCircle color="#22c55e" size={22} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }} />
              )}
              {newName && !checking && nameAvailable === false && (
                <FaTimesCircle color="#ef4444" size={22} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }} />
              )}
            </div>
            <button
              style={{
                width: '100%',
                padding: '0.75rem',
                margin: '1.5rem 0 0 0',
                borderRadius: '0.5rem',
                border: 'none',
                background: nameAvailable ? '#6366f1' : '#e5e7eb',
                color: nameAvailable ? '#fff' : '#888',
                fontWeight: 600,
                fontSize: '1rem',
                cursor: nameAvailable ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s',
              }}
              disabled={!newName || !nameAvailable}
              onClick={handleAddProject}
            >
              Add
            </button>
            <button
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'none',
                border: 'none',
                fontSize: 22,
                color: '#888',
                cursor: 'pointer',
              }}
              onClick={() => setShowPrompt(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OpenProjects; 